import React, { memo, useState, useMemo } from "react";
import Header from "../Components/Header";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";
import Footer from "../Components/Footer";

const ContactUs = () => {
  // ✅ Initial form data
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    city: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ✅ Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required.";
    if (!/^[0-9]{10}$/.test(formData.phone.trim()))
      newErrors.phone = "Enter a valid 10-digit phone number.";
    if (!formData.city.trim()) newErrors.city = "City is required.";
    return newErrors;
  };

  // ✅ Submit handler with API integration
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      console.warn("❌ Validation errors:", newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(
        "https://api.maalwahak.com/welcome/save_contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Form submitted successfully:", result);
      alert("Thank you! Your request has been submitted successfully.");

      // Reset form after successful submission
      setFormData({
        name: "",
        phone: "",
        company: "",
        city: "",
        message: "",
      });
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      alert(
        "Sorry, there was an error submitting your request. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Dynamic input fields (useMemo for performance)
  const formFields = useMemo(
    () => [
      { label: "Name *", name: "name", type: "text", required: true },
      { label: "Phone *", name: "phone", type: "tel", required: true },
      { label: "Company Name", name: "company", type: "text" },
      { label: "City *", name: "city", type: "text", required: true },
    ],
    []
  );

  // ✅ Contact Info Cards
  const contactInfo = useMemo(
    () => [
      {
        icon: Mail,
        title: "Email",
        lines: ["support@triosoft.ai", "sales@triosoft.ai"],
      },
      {
        icon: Phone,
        title: "Phone",
        lines: ["+91 7880016644", "Mon-Sat, 9AM-6PM IST"],
      },
      {
        icon: MapPin,
        title: "Head Office",
        lines: [
          "IT Park, B39B, Abbas Nagar, Gandhi Nagar",
          "Bhopal, Madhya Pradesh",
          "India - 462036",
        ],
      },
      {
        icon: MapPin,
        title: "Sales Office",
        lines: [
          "740, Phase 5, Udyog Vihar",
          "Gurugram, Haryana",
          "India - 122012",
        ],
      },
    ],
    []
  );

  return (
    <main className="bg-[var(--page-bg)] text-[var(--black)]">
      <Header />

      {/* ===== Hero Section ===== */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-[#fdfdfd] to-[#f6f8fa] text-center">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-8">
          <motion.h1
            className="font-heading text-[28px] lg:text-[48px] font-bold mb-5 leading-tight"
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            Get in Touch
          </motion.h1>

          <motion.p
            className="text-[16px] lg:text-[18px] text-[var(--gray-text)] max-w-[100%] lg:max-w-[75%] mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Have questions? We'd love to hear from you. Send us a message and
            we'll respond as soon as possible.
          </motion.p>
        </div>
      </section>

      {/* ===== Contact Form Section ===== */}
      <section className="py-10 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3 max-w-6xl mx-auto">
            {/* ===== Left: Contact Form ===== */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border bg-white p-8 shadow-sm">
                <h2 className="font-heading text-2xl font-bold mb-2">
                  Get In Touch
                </h2>
                <p className="text-muted-foreground mb-8">
                  Fill out the form below and our team will get back to you.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Input fields */}
                  <div className="grid gap-6 md:grid-cols-2">
                    {formFields.map((field) => (
                      <div key={field.name} className="space-y-2 text-left">
                        <label
                          htmlFor={field.name}
                          className="text-sm font-medium"
                        >
                          {field.label}
                        </label>
                        <input
                          id={field.name}
                          name={field.name}
                          type={field.type}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          disabled={isSubmitting}
                          className="flex h-9 w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] md:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        {errors[field.name] && (
                          <span className="text-[red] text-sm">
                            {errors[field.name]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Message Field */}
                  <div className="space-y-2 text-left">
                    <label htmlFor="message" className="text-sm font-medium">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="Tell us about your requirements..."
                      className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] md:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 rounded-md w-full bg-[var(--primary-color)] text-white py-3 font-medium hover:bg-[var(--primary-colorHover)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Request Demo"}
                  </button>
                </form>
              </div>
            </div>

            {/* ===== Right: Contact Info ===== */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div
                    key={index}
                    className="rounded-xl border bg-white p-6 shadow-sm flex items-start gap-4"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon className="h-5 w-5 text-[var(--primary-color)]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold mb-1">
                        {info.title}
                      </h3>
                      {info.lines.map((line, i) => (
                        <p
                          key={i}
                          className="text-sm text-muted-foreground leading-snug"
                        >
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default memo(ContactUs);
